/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Button } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';

import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedTextInput from 'bundles/page/components/TrackedTextInput';

import _t from 'i18n!nls/discussions';

type Props = {
  handleSubmit: () => void;
  handleChange: (x: string) => void;
  query: string;
  handleBlur: () => void;
  handleTab: (event: React.KeyboardEvent<HTMLElement>) => void;
  handleShiftTab: (event: React.KeyboardEvent<HTMLElement>) => void;
  handleClear: () => void;
  focus?: boolean;
  placeholder?: string;
};

class SearchBar extends React.Component<Props> {
  declare searchbox: HTMLInputElement | null | undefined;

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof SearchBar.contextTypes>;

  static defaultProps = {
    handleBlur: () => {},
    handleTab: () => {},
    handleShiftTab: () => {},
    handleClear: () => {},
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { handleChange } = this.props;
    const { value } = e.target;
    handleChange(value);
  };

  handleSubmitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // prevent blur event
    const { handleSubmit } = this.props;
    handleSubmit();
  };

  handleClear = () => {
    const { router } = this.context;
    const {
      location: { query, pathname },
      params,
    } = router;
    const { handleBlur, handleClear } = this.props;

    if (!query.q) {
      // No query so there is no active search yet, just blur it.
      handleBlur();
    }

    handleClear();
    // Otherwise there is an active search, transition to no search at all.
    delete query.q;
    router.push({
      pathname,
      params,
      query,
    });
  };

  handleBlur = () => {
    const { handleBlur } = this.props;
    if (handleBlur) {
      handleBlur();
    }
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { handleSubmit, handleBlur, handleShiftTab } = this.props;
    switch (e.key) {
      case 'Enter': {
        handleSubmit();
        break;
      }
      case 'Tab': {
        if (e.shiftKey && handleBlur) {
          handleBlur();
          handleShiftTab(e);
        }
        break;
      }
      default:
        break;
    }
  };

  handleClearKeyDown = (e: React.KeyboardEvent<SVGSVGElement>) => {
    const { handleBlur, handleShiftTab } = this.props;
    switch (e.key) {
      case 'Enter': {
        this.handleClear();
        break;
      }
      case 'Tab': {
        if (e.shiftKey && handleBlur) {
          handleBlur();
          handleShiftTab(e as unknown as React.KeyboardEvent<HTMLElement>);
        }
        break;
      }
      default:
        break;
    }
  };

  submitKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const { handleTab, handleShiftTab, handleSubmit } = this.props;
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        handleShiftTab(e);
      } else {
        handleTab(e);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      handleSubmit();
    }
  };

  render() {
    const { query, placeholder } = this.props;

    const closeIconStyle = css({
      flexBasis: '30px',
    });

    const searchbarContainerStyle = css({
      zIndex: 1000,
      border: 'none',
      gap: 'var(--cds-spacing-100)',
      '.input-area': {
        background: 'var(--cds-color-neutral-background-primary)',
        border: '1px solid var(--cds-color-grey-600)',
        boxSizing: 'border-box',
        borderRadius: '4px',
        '.search-input': {
          borderRadius: '4px',
          padding: '7px 12px',
        },
        '.cif-cancel-hint': {
          opacity: 1,
          margin: '0 0 0 var(--cds-spacing-200)',
        },
      },
    });

    const inputContainerStyle = css({
      display: 'flex',
    });

    const closeIcon = (
      <CloseIcon
        size="small"
        role="button"
        onMouseDown={this.handleClear}
        tabIndex={0}
        onKeyDown={this.handleClearKeyDown}
        aria-label={_t('Clear Search')}
        css={closeIconStyle}
        aria-hidden={false}
      />
    );

    return (
      <div
        className="rc-personalization__SearchBar search-bar expand-block expanded flex-1"
        css={searchbarContainerStyle}
      >
        <div className="input-area" css={inputContainerStyle}>
          <TrackedTextInput
            trackingName="search_bar"
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            value={query}
            placeholder={placeholder}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            ref={(comp) => {
              // @ts-expect-error ts-migrate(2322) FIXME: Type 'TrackedTextInput | null' is not assignable t... Remove this comment to see the full error message
              this.searchbox = comp;
            }}
            aria-label={_t('Search Input')}
            className="search-input"
          />
          {query && closeIcon}
        </div>
        <Button
          component={TrackedButton}
          variant="primary"
          size="small"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          onClick={this.handleSubmitClick}
          onMouseDown={this.handleSubmitClick}
          onKeyDown={this.submitKeyDown}
          onBlur={this.handleBlur}
          trackingName="search_forum"
          aria-label={_t('submit search')}
        >
          {_t('Search')}
        </Button>
      </div>
    );
  }
}

export default SearchBar;
